export type OperatorPresenceStatus = 'online' | 'lunch' | 'offline'

/** GET /api/operator/presence — текущий оператор (свой статус). */
export type OperatorOwnPresence = {
  userId: number | null
  status: OperatorPresenceStatus
  isOnBreak: boolean
  breakStartedAt: string | null
  breakUntil: string | null
  breakReason: string | null
}

function readIsoField(row: Record<string, unknown>, key: string): string | null {
  const v = row[key]
  return typeof v === 'string' && v.trim() ? v.trim() : null
}

function readOwnPresenceRow(row: Record<string, unknown>): OperatorOwnPresence | null {
  const status = resolvePresenceFromPayload(row)
  if (!status) return null

  const userIdRaw = Number(row.userId ?? row.operatorId ?? row.id)
  const userId = Number.isFinite(userIdRaw) ? userIdRaw : null
  const isOnBreak = row.isOnBreak === true || status === 'lunch'

  return {
    userId,
    status,
    isOnBreak,
    breakStartedAt: readIsoField(row, 'breakStartedAt'),
    breakUntil: readIsoField(row, 'breakUntil'),
    breakReason:
      typeof row.breakReason === 'string' && row.breakReason.trim() ? row.breakReason.trim() : null,
  }
}

/**
 * Парсит ответ GET /api/operator/presence:
 * `{ success: true, data: { userId, status, isOnBreak, breakStartedAt, breakUntil, breakReason } }`
 */
export function parseOperatorOwnPresenceGetResponse(
  json: unknown,
  currentUserId?: number,
): OperatorOwnPresence | null {
  if (json == null || typeof json !== 'object') return null

  const root = json as Record<string, unknown>
  if (root.success === false) return null

  const data = root.data
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null

  const row = data as Record<string, unknown>
  const hasTeamList = Array.isArray(row.operators) || Array.isArray(row.presences)
  if (hasTeamList) {
    if (currentUserId == null) return null
    const map = parseOperatorPresenceGetResponse(json)
    const st = map.get(currentUserId)
    if (!st) return null
    return {
      userId: currentUserId,
      status: st,
      isOnBreak: st === 'lunch',
      breakStartedAt: null,
      breakUntil: null,
      breakReason: null,
    }
  }

  const own = readOwnPresenceRow(row)
  if (!own) return null

  if (currentUserId != null && own.userId != null && own.userId !== currentUserId) {
    return null
  }

  return own
}

export function mapBackendStatusToUi(raw: unknown): OperatorPresenceStatus | null {
  if (typeof raw !== 'string') return null
  const s = raw.trim().toLowerCase()
  if (s === 'online') return 'online'
  if (s === 'offline') return 'offline'
  if (s === 'lunch' || s === 'break') return 'lunch'
  return null
}

export function normalizeOperatorPresenceStatus(raw: unknown): OperatorPresenceStatus | null {
  return mapBackendStatusToUi(raw)
}

export function resolvePresenceFromPayload(row: Record<string, unknown>): OperatorPresenceStatus | null {
  const fromStatus = mapBackendStatusToUi(row.status ?? row.presenceStatus ?? row.presence)
  if (fromStatus) return fromStatus
  if (row.isOnBreak === true) return 'lunch'
  if (row.isOnline === true) return 'online'
  if (row.isOnline === false) return 'offline'
  return null
}

export function parseOperatorPresenceGetResponse(json: unknown): Map<number, OperatorPresenceStatus> {
  const out = new Map<number, OperatorPresenceStatus>()
  if (json == null || typeof json !== 'object') return out

  const root = json as Record<string, unknown>
  const data = (root.data ?? root) as Record<string, unknown>

  const put = (id: unknown, value: unknown) => {
    const n = Number(id)
    if (!Number.isFinite(n)) return
    let st: OperatorPresenceStatus | null = null
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      st = resolvePresenceFromPayload(value as Record<string, unknown>)
    } else {
      st = mapBackendStatusToUi(value)
    }
    if (st) out.set(n, st)
  }

  if (data && typeof data === 'object') {
    if (Array.isArray(data.operators)) {
      for (const row of data.operators) {
        if (!row || typeof row !== 'object') continue
        const r = row as Record<string, unknown>
        put(r.id ?? r.userId, r)
      }
      return out
    }
    if (Array.isArray(data.presences)) {
      for (const row of data.presences) {
        if (!row || typeof row !== 'object') continue
        const r = row as Record<string, unknown>
        put(r.userId ?? r.operatorId ?? r.id, r)
      }
      return out
    }

    for (const [k, v] of Object.entries(data)) {
      if (/^\d+$/.test(k)) {
        put(k, v)
        continue
      }
      if (k === 'byUserId' || k === 'map' || k === 'users') {
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          for (const [id, st] of Object.entries(v as Record<string, unknown>)) put(id, st)
        }
      }
    }
  }

  return out
}

function unwrapWsPresencePayload(raw: unknown): Record<string, unknown> | null {
  let cur: unknown = raw
  if (typeof cur === 'string') cur = safeJsonParse(cur)
  if (!cur || typeof cur !== 'object') return null

  let o = cur as Record<string, unknown>

  const pub = o.pub
  if (pub && typeof pub === 'object') {
    const inner = (pub as Record<string, unknown>).data
    if (inner && typeof inner === 'object') {
      o = inner as Record<string, unknown>
    }
  }

  if (o.type === 'operator.presence.updated' && o.data && typeof o.data === 'object' && !Array.isArray(o.data)) {
    return o.data as Record<string, unknown>
  }

  if (o.userId != null || o.status != null || o.isOnline != null || o.isOnBreak != null) {
    return o
  }

  return null
}

export function parseOperatorPresenceWsPayload(raw: unknown): {
  userId: number
  status: OperatorPresenceStatus
} | null {
  const inner = unwrapWsPresencePayload(raw)
  if (!inner) return null

  const userId = Number(inner.userId ?? inner.operatorId ?? inner.id)
  const status = resolvePresenceFromPayload(inner)
  if (!Number.isFinite(userId) || !status) return null
  return { userId, status }
}

/** WS operator.<id>.presence — полный статус текущего оператора. */
export function parseOperatorOwnPresenceWsPayload(
  raw: unknown,
  currentUserId?: number,
): OperatorOwnPresence | null {
  const inner = unwrapWsPresencePayload(raw)
  if (!inner) return null
  if (currentUserId != null) {
    const userId = Number(inner.userId ?? inner.operatorId ?? inner.id)
    if (Number.isFinite(userId) && userId !== currentUserId) return null
  }
  return readOwnPresenceRow(inner)
}

function safeJsonParse(s: string): unknown {
  try {
    return JSON.parse(s)
  } catch {
    return null
  }
}
